import { Injectable } from '@angular/core';
import { Platform } from '@ionic/angular';
// import { Auth } from '@angular/fire/auth'; // Temporarily disabled
import { FirebaseAnalytics } from '@capacitor-firebase/analytics';
import { FirebaseAuthentication } from '@capacitor-firebase/authentication';
import { initializeApp, getApps } from 'firebase/app';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
/**
 * Service for initializing Firebase and ensuring it's ready before use
 */
export class FirebaseInitService {
  private isInitialized = false;
  private initPromise: Promise<void> | null = null;
  private isInitializing = false;

  constructor(
    private platform: Platform
    // private fbAuth: Auth // Temporarily disabled
  ) {
    // Start initialization immediately but don't block constructor
    console.log('FirebaseInitService constructor called');
    this._startInitialization();
  }

  private async _startInitialization(): Promise<void> {
    if (this.isInitializing || this.isInitialized) {
      console.log('Firebase initialization already in progress or completed');
      return;
    }
    
    console.log('Starting Firebase initialization...');
    this.isInitializing = true;
    try {
      await this._doInitialize();
      this.isInitialized = true;
      console.log('Firebase initialization completed successfully');
    } catch (error) {
      console.error('Firebase initialization failed:', error);
      // Don't set isInitialized to false, allow retry
    } finally {
      this.isInitializing = false;
    }
  }

  async initialize(): Promise<void> {
    if (this.isInitialized) {
      return;
    }

    if (this.initPromise) {
      return this.initPromise;
    }

    this.initPromise = this._doInitialize();
    await this.initPromise;
    this.isInitialized = true;
  }

  private async _doInitialize(): Promise<void> {
    try {
      // Wait for platform to be ready
      await this.platform.ready();
      
      // Ensure Firebase app is initialized
      await this.ensureFirebaseAppInitialized();
      
      // Add a longer delay to ensure all services are ready
      await new Promise(resolve => setTimeout(resolve, 2000));

      if (this.platform.is('capacitor') || this.platform.is('cordova')) {
        // For Capacitor, ensure Firebase plugins are available
        console.log('Initializing Capacitor Firebase plugins...');
        
        // Test Firebase Authentication plugin availability
        // Note: We don't call getIdToken() here because it requires a logged-in user
        // Instead, we just check if getCurrentUser() is available (which doesn't require a user)
        let authReady = false;
        for (let i = 0; i < 3; i++) {
          try {
            // Use getCurrentUser() instead of getIdToken() - it doesn't require a logged-in user
            // and just returns null if no user is logged in, which is fine for initialization
            await FirebaseAuthentication.getCurrentUser();
            authReady = true;
            break;
          } catch (error: any) {
            // Only log if it's not a "no user" error
            const errorMessage = error?.message || error?.errorMessage || '';
            if (!errorMessage.includes('RuntimeError') && !errorMessage.includes('no user')) {
              console.warn(`Firebase Authentication attempt ${i + 1} failed:`, error);
            }
            await new Promise(resolve => setTimeout(resolve, 500));
          }
        }
        
        if (!authReady) {
          console.warn('Firebase Authentication plugin not ready after retries - continuing anyway');
        }

        // Test Firebase Analytics with retry logic
        let analyticsReady = false;
        for (let i = 0; i < 3; i++) {
          try {
            console.log(`Testing Firebase Analytics (attempt ${i + 1})...`);
            await FirebaseAnalytics.setEnabled({ enabled: true });
            analyticsReady = true;
            console.log('Firebase Analytics ready');
            break;
          } catch (error) {
            console.warn(`Firebase Analytics attempt ${i + 1} failed:`, error);
            await new Promise(resolve => setTimeout(resolve, 500));
          }
        }
        
        if (!analyticsReady) {
          console.warn('Firebase Analytics not ready after retries - continuing anyway');
        }
      } else {
        // For web, ensure AngularFire services are ready
        console.log('Initializing AngularFire services...');
        
        // Test AngularFire Auth with retry logic
        let authReady = false;
        for (let i = 0; i < 3; i++) {
          try {
            console.log(`Testing AngularFire Auth (attempt ${i + 1})...`);
            // For modular API, we don't test authState directly
            // Just check if the service is available
            // Note: AngularFire Auth temporarily disabled due to initialization issues
            console.log('AngularFire Auth temporarily disabled');
            authReady = true;
            break;
          } catch (error) {
            console.warn(`AngularFire Auth attempt ${i + 1} failed:`, error);
            await new Promise(resolve => setTimeout(resolve, 500));
          }
        }
        
        if (!authReady) {
          console.warn('AngularFire Auth not ready after retries - continuing anyway');
        }

        // Test AngularFire Analytics with retry logic
        let analyticsReady = false;
        for (let i = 0; i < 3; i++) {
          try {
            console.log(`Testing AngularFire Analytics (attempt ${i + 1})...`);
            // For modular API, setAnalyticsCollectionEnabled is not available
            // Analytics collection is controlled via Firebase console
            console.log('Analytics collection enabled via Firebase console');
            analyticsReady = true;
            console.log('AngularFire Analytics ready');
            break;
          } catch (error) {
            console.warn(`AngularFire Analytics attempt ${i + 1} failed:`, error);
            await new Promise(resolve => setTimeout(resolve, 500));
          }
        }
        
        if (!analyticsReady) {
          console.warn('AngularFire Analytics not ready after retries - continuing anyway');
        }
      }

      console.log('Firebase services initialization completed');
    } catch (error) {
      console.error('Firebase initialization failed:', error);
      // Don't throw error, just log it and continue
    }
  }

  isReady(): boolean {
    return this.isInitialized;
  }

  async waitForReady(): Promise<void> {
    if (this.isInitialized) {
      console.log('Firebase is already ready');
      return;
    }
    
    // If initialization is in progress, wait for it
    if (this.isInitializing) {
      console.log('Firebase initialization in progress, waiting...');
      let attempts = 0;
      while (this.isInitializing && attempts < 100) { // Wait up to 10 seconds
        await new Promise(resolve => setTimeout(resolve, 100));
        attempts++;
      }
    }
    
    if (!this.isInitialized) {
      console.log('Firebase not initialized yet, starting initialization...');
      await this.initialize();
    }
    
    // Additional wait to ensure services are truly ready
    console.log('Additional wait to ensure services are ready...');
    await new Promise(resolve => setTimeout(resolve, 500));
  }

  // Ensure Firebase app is initialized
  private async ensureFirebaseAppInitialized(): Promise<void> {
    try {
      // Check if Firebase app is already initialized
      const apps = getApps();
      if (apps.length > 0) {
        console.log('Firebase app already initialized:', apps[0].name);
        return;
      }
      
      console.log('Firebase app not initialized, initializing manually...');
      const app = initializeApp(environment.firebaseConfig);
      console.log('Firebase app initialized manually:', app.name);
    } catch (error) {
      console.error('Failed to initialize Firebase app manually:', error);
      // Don't throw error, just log it
    }
  }

  // Safe wrapper for Firebase calls
  async safeFirebaseCall<T>(call: () => Promise<T>, fallback?: T): Promise<T | undefined> {
    try {
      await this.waitForReady();
      
      // Additional check to ensure Firebase is truly ready
      if (!this.isInitialized) {
        console.warn('Firebase still not ready after waitForReady');
        return fallback;
      }
      
      return await call();
    } catch (error) {
      console.warn('Firebase call failed:', error);
      return fallback;
    }
  }
}

